import React, { useState } from 'react';
import Modal from '../shared/Modal';
import { useNavigate } from 'react-router-dom';
import { Mold, MoldCreationData } from '../../types/mold';
import { useMold } from '../../hooks/useMold';
import { useAuth } from '../../hooks/useAuth';
import StepIndicator from '../CreateMoldOrder/StepIndicator';
import OutlinedMoldIcon from '../icons/OutlinedMoldIcon';
import NewMoldInformation from './NewMoldInformation';
import ValidationErrors from '../shared/ValidationErrors';
import AdminStatusToggle from './AdminStatusToggle';
import { InfoIcon } from 'lucide-react';
import { getNestedValue, getUpdatedMold } from '../../utils/object';
import { useAppState } from '../../context/AppStateContext';

interface CreateNewMoldProps {
}

const CreateNewMold: React.FC<CreateNewMoldProps> = () => {
    const navigate = useNavigate();
    const { featureToggles, getMoldFormConfiguration } = useAppState();
    const { createMold, loading, error } = useMold();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(1);
    const [createWithActiveStatus, setCreateWithActiveStatus] = useState(featureToggles.enableNewMoldWithReadyStatus);
    const [newMold, setNewMold] = useState<Partial<Mold>>({
        sbiId: '',
        moldNumber: '',
        customerId: undefined,
        revision: 1,
        cycleShotCount: 0,
        gateType: '',
        hotRunnerType: '',
        hotRunnerBrand: '',
        qtyZoneHr: '',
        cavity: '',
        plate: '',
        insertEngraving: false,
        insertChangeType: '',
        currentLocation: '',
        storageLocation: '',
        assetNoCustomer: '',
        shotCount: 0,
        status: 'active',
        shotThreshold: 0,
        maxShotCount: undefined,
        productParts: [],
        productName: '',
        physicalShotCount: 0,
        extraAttributes: {},
    });
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');

    const moldFormConfiguration = getMoldFormConfiguration();

    // Step-based validation logic with configurable error display
    type ValidationConfig = {
        validate: (mold?: Partial<Mold>) => { errors: string[]; fieldErrors?: Record<string, boolean> };
        showModal?: boolean;
    };

    const stepValidations: { [step: number]: ValidationConfig } = {
        1: {
            showModal: true,
            validate: (moldArg?: Partial<Mold>) => {
                const mold = moldArg ?? newMold;
                const errors: string[] = [];
                if (!mold.productParts || mold.productParts.length === 0) {
                    errors.push('กรุณาเพิ่มชิ้นส่วนอย่างน้อย 1 รายการ');
                }
                return { errors };
            }
        },
        2: {
            showModal: false,
            validate: (moldArg?: Partial<Mold>) => {
                const mold = moldArg ?? newMold;
                const errors: string[] = [];
                const fieldErrors: Record<string, boolean> = {};
                const generalFields = moldFormConfiguration['general'] || [];
                generalFields.forEach(field => {
                    if (field.required) {
                        const key = String(field.key);
                        const value = getNestedValue(mold, key);
                        if (
                            value === null ||
                            value === undefined ||
                            (typeof value === 'string' && value.trim() === '')
                        ) {
                            errors.push(`กรุณากรอก ${field.label}`);
                            fieldErrors[key] = true;
                        }
                    }
                });
                return { errors, fieldErrors };
            }
        },
        // Step 4 is review/submit, no validation needed here
    };

    const handleFieldChange = (field: keyof Mold | string, value: any) => {
        if (fieldErrors[field as string]) {
            setFieldErrors((prev) => ({
                ...prev,
                [field]: false,
            }));
            setValidationErrors((prev) => {
                const allFields = Object.values(moldFormConfiguration).flat();
                const fieldConfig = allFields.find(f => f.key === field);
                if (!fieldConfig) return prev;
                const label = fieldConfig.label;
                return prev.filter(msg => !msg.includes(label));
            });
        }

        const updatedMold = getUpdatedMold(newMold, field, value);

        Object.values(stepValidations).forEach((config) => {
            if (!config.validate) return;
            const { errors } = config.validate(updatedMold);
            setValidationErrors((prev) => prev.filter(msg => errors.includes(msg)));
        });

        setNewMold(prev => getUpdatedMold(prev, field, value));
    };

    const handleSubmit = async () => {
        if (currentStep < 4) {
            // Run step-specific validation if exists
            const config = stepValidations[currentStep];
            if (config) {
                const { errors, fieldErrors } = config.validate();
                setValidationErrors(errors);
                setFieldErrors(fieldErrors || {});
                if (errors.length > 0) {
                    if (config.showModal) {
                        setErrorModalMessage(errors.join('\n'));
                        setShowErrorModal(true);
                    }
                    // If not modal, just show inline error
                    return;
                }
            }
            setCurrentStep(currentStep + 1);
            return;
        }

        const { customer, productParts, ...rest } = newMold;

        // Correctly extract just the IDs from product parts
        const productPartIDs = productParts?.map(part => part.id) || [];
        const insertIds = rest.inserts?.map(insert => insert.id) || [];

        // Determine status based on admin toggle or default to inactive
        const moldStatus = (user?.role === 'ADMIN' && createWithActiveStatus) ? 'active' : 'inactive';

        const creationData: MoldCreationData = {
            ...rest,
            revision: 1,
            customerId: newMold.customerId ?? customer!.id,
            productPartIds: productPartIDs,
            insertIds: insertIds,
            status: moldStatus
        };

        const createdMoldResult = await createMold(creationData);
        const newCreatedMold = createdMoldResult?.isSuccess ? createdMoldResult.data : null;
        if (newCreatedMold) {
            navigate(`/molds/${newCreatedMold.id}`);
        } else {
            const displayMsg = createdMoldResult && !createdMoldResult.isSuccess
                ? createdMoldResult.displayErrorMessage
                : null;
            setErrorModalMessage(displayMsg ?? 'เกิดข้อผิดพลาดในการสร้างแม่พิมพ์ กรุณาลองใหม่อีกครั้ง');
            setShowErrorModal(true);
        }
    };

    return (
        <div className="bg-gray min-h-screen">
            <header
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-4 mb-6 sm:mb-8 rounded-b-xl shadow-lg gap-4 sm:gap-0"
                style={{
                    // Recommended: Use a blue gradient that matches Tailwind's blue-600 to blue-900 for a modern, clean look
                    background: 'linear-gradient(90deg, #2563eb 0%, #1e40af 100%)',
                }}
            >
                <div className="flex items-end gap-3">
                    <OutlinedMoldIcon color="#ffffff" size={36} strokeWidth={60} showHolders={false} />
                    <div className="flex flex-col">
                        <div className="text-lg font-semibold text-white drop-shadow">
                            สร้างแม่พิมพ์ใหม่
                        </div>
                        {newMold.productParts && newMold.productParts.length > 0 && <span className="text-white text-xs opacity-80 mb-0.5">
                            {newMold.productParts.map(p => p.partNumber).join(', ')}
                        </span>}
                    </div>
                </div>

                {/* Admin Status Toggle */}
                {user?.role === 'ADMIN' && (
                    <AdminStatusToggle
                        isActive={createWithActiveStatus}
                        onChange={setCreateWithActiveStatus}
                    />
                )}
            </header>
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex justify-start gap-4 mb-6 sm:mb-8">
                    <StepIndicator
                        step={1}
                        currentStep={currentStep}
                        label="ชิ้นส่วน"
                        onClick={() => setCurrentStep(1)}
                        disabled={currentStep < 1}
                    />
                    <StepIndicator
                        step={2}
                        currentStep={currentStep}
                        label="ข้อมูลแม่พิมพ์เบื้องต้น"
                        onClick={() => setCurrentStep(2)}
                        disabled={currentStep < 2}
                    />
                    <StepIndicator
                        step={3}
                        currentStep={currentStep}
                        label="อินเสิร์ท"
                        onClick={() => setCurrentStep(3)}
                        disabled={currentStep < 3}
                    />
                    <StepIndicator
                        step={4}
                        currentStep={currentStep}
                        label="ตรวจสอบและยืนยัน"
                        onClick={() => setCurrentStep(4)}
                        disabled={currentStep < 4}
                    />
                </div>
                <div className="bg-white flex flex-col shadow-md rounded-lg px-5 sm:px-6 pb-6 pt-2">
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                            <span className="block sm:inline">{error}</span>
                        </div>
                    )}
                    {validationErrors.length > 0 && (
                        <ValidationErrors errors={validationErrors} />
                    )}

                    {/* Inline error modal for step validation */}
                    {showErrorModal && (
                        <Modal data-testid="modal" onClose={() => setShowErrorModal(false)} isBackgroundTransparent={true} hideTopPadding={true}>
                            <div className="flex flex-col items-center">
                                <div className="flex flex-row items-center justrify-center mb-4 gap-1">
                                    <InfoIcon className="inline text-red-600" size={24} />
                                    <div className="text-xl font-semibold text-red-600">พบข้อผิดพลาด</div>
                                </div>
                                <div className="text-gray-800 whitespace-pre-line">{errorModalMessage}</div>
                                <div className="mt-4 flex justify-end w-full">
                                    <button
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full"
                                        onClick={() => setShowErrorModal(false)}
                                    >ปิด</button>
                                </div>
                            </div>
                        </Modal>
                    )}

                    {/* Status Information for Admin in Review Step */}
                    {user?.role === 'ADMIN' && currentStep === 4 && (
                        <div className={`mb-6 p-5 rounded-xl border-2 transition-all duration-300 ${createWithActiveStatus
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                            : 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200'
                            }`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full ${createWithActiveStatus ? 'bg-green-500' : 'bg-orange-500'
                                        }`} />
                                    <div className={`font-semibold ${createWithActiveStatus ? 'text-green-800' : 'text-orange-800'
                                        }`}>
                                        สถานะแม่พิมพ์ที่จะถูกสร้าง
                                    </div>
                                </div>
                                <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border-2 ${createWithActiveStatus
                                    ? 'bg-green-100 text-green-800 border-green-300'
                                    : 'bg-orange-100 text-orange-800 border-orange-300'
                                    }`}>
                                    <span className={`w-2 h-2 mr-2 rounded-full ${createWithActiveStatus ? 'bg-green-500' : 'bg-orange-500'
                                        }`} />
                                    {createWithActiveStatus ? 'พร้อมใช้งาน (Active)' : 'ไม่พร้อมใช้งาน (Inactive)'}
                                </div>
                            </div>
                            <div className={`mt-3 p-3 rounded-lg ${createWithActiveStatus ? 'bg-green-100/50' : 'bg-orange-100/50'
                                }`}>
                                <div className={`text-sm font-medium ${createWithActiveStatus ? 'text-green-700' : 'text-orange-700'
                                    }`}>
                                    {createWithActiveStatus
                                        ? '✓ แม่พิมพ์จะถูกสร้างในสถานะพร้อมใช้งานทันที สามารถนำไปใช้ในการผลิตได้ทันที'
                                        : '⚠ แม่พิมพ์จะถูกสร้างในสถานะไม่พร้อมใช้งาน จำเป็นต้องผ่านกระบวนการตรวจสอบและอนุมัติก่อนใช้งาน'}
                                </div>
                            </div>
                        </div>
                    )}

                    <NewMoldInformation
                        isEditing={currentStep < 4}
                        onFieldChange={handleFieldChange}
                        editedMold={newMold as Mold}
                        mold={newMold as Mold}
                        layout="expanded"
                        fieldErrors={fieldErrors}
                        viewMode={currentStep < 4 ? 'tabs' : 'review'}
                        currentStep={currentStep}
                        moldFormConfiguration={moldFormConfiguration}
                    />

                    <div className="flex flex-row gap-2 px-0">
                        <button
                            className={`w-1/2 self-center text-xl ${currentStep === 1 ? 'bg-gray-300' : 'bg-gray-500 hover:bg-gray-600'} text-white px-4 py-4 rounded-lg mt-4`}
                            onClick={() => {
                                setCurrentStep(prev => Math.max(1, prev - 1));
                                setValidationErrors([]);
                                setFieldErrors({});
                            }}
                            disabled={currentStep === 1}
                        >
                            ย้อนกลับ
                        </button>
                        <button
                            className='w-1/2 self-center text-xl bg-blue-600 hover:bg-blue-700 text-white px-4 py-4 rounded-lg mt-4'
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'กำลังบันทึก...' : currentStep === 4 ? 'บันทึก' : 'ถัดไป'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateNewMold;